import { Link } from 'react-router-dom';
import { Sparkles, ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-indigo-700 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-serif font-bold text-slate-900">StatsXY</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-indigo-600">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-slate-900 mb-2">
            Terms and Conditions of Use
          </h1>
          <p className="text-lg text-indigo-600 mb-8">
            StatsXY – AI-Powered Statistics & Research Analytics Platform
          </p>

          <div className="prose prose-slate max-w-none">
            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">1. INTRODUCTION</h2>
            <p className="text-slate-700 mb-4">
              These Terms and Conditions ("Terms") govern the access to and use of StatsXY, an AI-powered statistics and research analytics platform ("StatsXY", "the Platform", "we", "us", or "our").
            </p>
            <p className="text-slate-700 mb-4">
              By accessing, registering for, subscribing to, or using StatsXY, you ("User", "you", or "your") agree to be legally bound by these Terms.
            </p>
            <p className="text-slate-700 mb-4 font-medium">
              If you do not agree to these Terms, you must not use the Platform.
            </p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">2. PURPOSE OF THE PLATFORM</h2>
            <p className="text-slate-700 mb-4">StatsXY is designed to assist students, academics, and researchers in:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Analyzing quantitative and qualitative research data</li>
              <li>Selecting appropriate statistical or qualitative analysis techniques</li>
              <li>Generating charts, tables, and visual outputs</li>
              <li>Providing educational explanations and interpretative guidance</li>
            </ul>
            <p className="text-slate-700 mb-4">StatsXY is intended as an assistive academic tool only.</p>
            <p className="text-indigo-700 font-medium mb-4 bg-indigo-50 p-3 rounded-lg">
              👉 StatsXY does not replace academic judgment, methodological expertise, or professional supervision.
            </p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">3. ELIGIBILITY</h2>
            <p className="text-slate-700 mb-4">You may use StatsXY only if:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>You are at least 18 years old, or</li>
              <li>You are legally permitted to use online academic tools in your jurisdiction</li>
            </ul>
            <p className="text-slate-700 mb-4">By using StatsXY, you confirm that you meet these requirements.</p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">4. USER RESPONSIBILITIES</h2>
            <p className="text-slate-700 mb-4">You agree that:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>You are solely responsible for the accuracy, completeness, and legality of all data you upload</li>
              <li>You understand your research context, methodology, and academic requirements</li>
              <li>You will use StatsXY ethically and responsibly, in accordance with your institution's academic integrity policies</li>
            </ul>
            <p className="text-slate-700 mb-4 font-medium">
              You acknowledge that final responsibility for research decisions, interpretations, and conclusions lies with you, not StatsXY.
            </p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">5. NO GUARANTEE OF RESULTS</h2>
            <p className="text-slate-700 mb-4">While StatsXY uses AI-driven logic and statistical methods to assist with analysis:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>We do not guarantee that outputs are error-free</li>
              <li>We do not guarantee suitability for any specific academic, institutional, or publication requirement</li>
              <li>Outputs may vary depending on data quality, structure, and research design</li>
            </ul>
            <p className="text-slate-700 mb-4 font-medium">
              StatsXY provides best-effort analytical assistance, not definitive or authoritative conclusions.
            </p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">6. LIMITATION OF LIABILITY</h2>
            <p className="text-slate-700 mb-4">To the fullest extent permitted by law:</p>
            <p className="text-slate-700 mb-4">StatsXY, its owners, developers, affiliates, and partners shall not be liable for:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Any academic penalties, grading outcomes, or institutional decisions</li>
              <li>Errors, omissions, or misinterpretations arising from use of the Platform</li>
              <li>Loss of data, research time, or academic opportunity</li>
              <li>Direct, indirect, incidental, consequential, or special damages</li>
            </ul>
            <p className="text-red-600 font-medium mb-4">Use of StatsXY is entirely at your own risk.</p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">7. ACADEMIC INTEGRITY & ETHICAL USE</h2>
            <p className="text-slate-700 mb-4">
              StatsXY is designed to support learning and correct methodology, not to facilitate misconduct.
            </p>
            <p className="text-slate-700 mb-4">You agree that you will not:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Submit StatsXY outputs as original work without proper understanding</li>
              <li>Use the Platform to engage in plagiarism, falsification, or data manipulation</li>
              <li>Misrepresent AI-generated outputs as human-only work where disclosure is required</li>
            </ul>
            <p className="text-slate-700 mb-4 font-medium">
              StatsXY does not assume responsibility for violations of academic integrity policies by users.
            </p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">8. DATA OWNERSHIP & PRIVACY</h2>
            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">8.1 User Data</h3>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>You retain ownership of all data you upload</li>
              <li>StatsXY does not claim ownership over your research data</li>
            </ul>
            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">8.2 Data Processing</h3>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Data is processed solely for the purpose of providing analytical services</li>
              <li>Reasonable security measures are implemented to protect user data</li>
            </ul>
            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">8.3 Confidentiality</h3>
            <p className="text-slate-700 mb-4">
              StatsXY does not intentionally share user data with third parties, except where required by law or for essential platform operations (e.g., payment processing).
            </p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">9. PURCHASES & PAYMENTS</h2>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>StatsXY operates on a time-based access model with one-time payments</li>
              <li>Payments are processed securely via PayPal (card processing)</li>
              <li>Access remains active for the purchased period from the date of purchase</li>
              <li>Upon expiry, access is restricted until a new purchase is made</li>
              <li>No refunds are provided for unused access periods unless required by law</li>
            </ul>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">10. SERVICE AVAILABILITY</h2>
            <p className="text-slate-700 mb-4">
              StatsXY aims for high availability but does not guarantee uninterrupted access.
            </p>
            <p className="text-slate-700 mb-4">We reserve the right to:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Modify or discontinue features</li>
              <li>Perform maintenance</li>
              <li>Temporarily suspend access for technical or security reasons</li>
            </ul>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">11. INTELLECTUAL PROPERTY</h2>
            <p className="text-slate-700 mb-4">
              All platform content, design, algorithms, workflows, and branding associated with StatsXY are the intellectual property of StatsXY unless otherwise stated.
            </p>
            <p className="text-slate-700 mb-4">You may not:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Copy, reverse-engineer, or resell the platform</li>
              <li>Use StatsXY branding without permission</li>
            </ul>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">12. TERMINATION</h2>
            <p className="text-slate-700 mb-4">We reserve the right to suspend or terminate access if:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>These Terms are violated</li>
              <li>The Platform is used for unlawful or abusive purposes</li>
              <li>There is misuse affecting system integrity</li>
            </ul>
            <p className="text-slate-700 mb-4">Termination does not waive outstanding payment obligations.</p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">13. CHANGES TO TERMS</h2>
            <p className="text-slate-700 mb-4">StatsXY may update these Terms from time to time.</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Updated Terms will be posted on the Platform</li>
              <li>Continued use constitutes acceptance of revised Terms</li>
            </ul>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">14. GOVERNING LAW</h2>
            <p className="text-slate-700 mb-4">
              These Terms shall be governed by and interpreted in accordance with the laws of Mauritius.
            </p>

            <hr className="my-6" />

            <div className="bg-indigo-50 p-6 rounded-xl mt-8">
              <h2 className="text-xl font-semibold text-indigo-900 mb-3">FINAL STATEMENT</h2>
              <p className="text-indigo-800 mb-2">
                StatsXY is designed to empower better research decisions, not to replace academic expertise.
              </p>
              <p className="text-indigo-800 font-medium">
                By using StatsXY, you acknowledge that you remain fully responsible for your research outcomes.
              </p>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <Link to="/privacy" className="hover:text-indigo-600 mx-3">Privacy Policy</Link>
          <span>|</span>
          <Link to="/contact" className="hover:text-indigo-600 mx-3">Contact Us</Link>
        </div>
      </main>
    </div>
  );
};

export default TermsPage;
